import type {
  CandidateProfile, CandidateStatement, CandidateEndorsement, CandidateMeasurePosition, RunningMate,
} from '../../citizen/domain/candidateProfiles';

export type { CandidateProfile, CandidateStatement, CandidateEndorsement, CandidateMeasurePosition, RunningMate };

export interface OfficeholderData {
  title: string;
  party: string | null;
  start_date: string | null;
  /** person_roles.term_end — when the current term ends, where recorded. */
  term_end?: string | null;
  organization: string | null;
  jurisdiction: string | null;
}

export interface SocialMediaAccount {
  platform: string;
  handle: string | null;
  url: string;
}

export interface PersonData {
  person_id: string;
  full_name: string;
  first_name: string;
  last_name: string;
  photo_url: string | null;
  roles: ('legislator' | 'candidate' | 'officeholder')[];
  legislator: LegislatorData | null;
  candidate: CandidateData | null;
  officeholder: OfficeholderData | null;
  campaign_finance: PersonCampaignFinance | null;
  social_media?: SocialMediaAccount[];
  contacts?: {
    email?: string | null;
    phone?: string | null;
    website?: string | null;
    social_media?: SocialMediaAccount[];
    /** Every public contact, grouped by role (CITIZEN_PERSON_PAGE_AND_COMMITTEE_CARD_PRD §5.3). */
    office?: ContactGroup | null;
    campaign?: ContactGroup | null;
  } | null;
}

/** One role's contacts, in the order to try them. Older payloads omit these. */
export interface ContactGroup {
  email: string[];
  phone: string[];
  website: string[];
}

/** How the person's committee was found; 'name_fallback' is a last-name guess and may be someone else's. */
export type CommitteeMatch = 'linked' | 'name' | 'name_fallback';

/**
 * A person's campaign money: TRACER for state candidacies, FEC for federal ones
 * (`candidates.level`). Never both — narrow on `source` with isFederalFinance.
 */
export type PersonCampaignFinance = CampaignFinanceData | FederalFinanceData;

/** OpenFEC candidate cycle totals; every figure is null until imported. */
export interface FederalFinanceTotals {
  receipts: number | null;
  individual_contributions: number | null;
  disbursements: number | null;
  cash_on_hand: number | null;
  debts_owed: number | null;
}

export interface FederalFinanceData {
  source: 'FEC';
  level: 'FEDERAL';
  fec_candidate_id: string | null;
  /** The FEC two-year cycle (an even year). */
  cycle: number | null;
  /** The principal campaign committee, once OpenFEC has been imported. */
  committee: { committee_id: string; name: string | null; fec_url: string } | null;
  totals: FederalFinanceTotals;
  coverage_start_date: string | null;
  /** The last day the newest FEC report covers; null when nothing is imported. */
  coverage_end_date: string | null;
  /** The candidate's page on fec.gov. */
  fec_url: string | null;
}

export function isFederalFinance(finance: PersonCampaignFinance | null | undefined): finance is FederalFinanceData {
  return finance?.source === 'FEC';
}

export interface CampaignFinanceData {
  source?: 'TRACER';
  level?: 'STATE';
  committee_name: string;
  committee_purpose?: string | null;
  /** TRACER committee id — opens the committee card. */
  co_id?: string | null;
  committee_match?: CommitteeMatch;
  finance_by_year: Record<number, FinanceCycle>;
}

export interface LegislatorData {
  id: number;
  chamber: string;
  district: string;
  party_affiliation: string;
  session_year: string;
  status: string;
  committees: CommitteeMembership[];
  stats: {
    sponsored: SponsoredStats;
    voting: VotingRecord;
  };
  contacts: {
    email?: string | null;
    phone?: string | null;
    website?: string | null;
    social_media?: SocialMediaAccount[];
  };
}

export interface CommitteeMembership {
  committee_name: string;
  chamber: string;
  role: string;
}

export interface SponsoredStats {
  total: number;
  passed: number;
  failed: number;
  vetoed: number;
  bipartisan: number;
}

export interface VoteCategory {
  key: string;
  label: string;
  count: number;
  percent: number;
}

export interface VotingStats {
  totalVotes: number;
  categories: VoteCategory[];
}

/**
 * A voting record, by period. Both use the same counting rule (final passage),
 * so they differ only in how much time they cover — which is what makes the
 * session/career toggle a change of question rather than of arithmetic.
 */
export interface VotingRecord {
  session: VotingStats & { sessionKey: string | null; label: string | null };
  career: VotingStats;
}

export interface ElectionMilestone {
  type: string;
  date: string;
  label: string;
  sort_order: number;
}

export interface ElectionCycle {
  id: string;
  name: string;
  election_type: string;
  milestones: ElectionMilestone[];
}

export interface CandidateData {
  id: string;
  office_sought: string;
  district?: string | null;
  district_display?: string | null;
  party_affiliation?: string | null;
  election_year: number;
  status: string;
  is_incumbent: boolean;
  seat_incumbent_name?: string | null;
  seat_incumbent_id?: number | null;
  election_cycle: ElectionCycle | null;
  campaign_finance_committee_name?: string | null;
  voluntary_spending_limits?: boolean;
  /** The SOS candidate affidavit date — when the candidacy was declared. */
  affidavit_filed_date?: string | null;
  finance_by_year: Record<number, FinanceCycle>;
  opponents: Opponent[];
  /** Published profile content (CANDIDATE_PROFILES_PRD.md §5.5.1); null = "Official record only". */
  profile: CandidateProfile | null;
  statements: CandidateStatement[];
  /** Sourced endorsements from the profile tables — replaces the never-read string list. */
  endorsements: CandidateEndorsement[];
  measure_positions: CandidateMeasurePosition[];
  running_mate: RunningMate | null;
  prior_offices: string[];
}

export interface FinanceCycle {
  total_raised: number;
  contribution_count: number;
  cash_on_hand: number;
  last_transaction_date: string | null;
  top_contributors: Contributor[];
}

export interface Contributor {
  name: string;
  city: string;
  amount: number;
}

export interface Opponent {
  person_id?: string;
  name: string;
  party: string;
  status: string;
  incumbent?: boolean;
}
